import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../context';
import type { CallType, KbItem } from '../types';

type Kind = KbItem['kind'];
const KINDS: Kind[] = ['question', 'action', 'info'];

/**
 * Manager/Admin: call types and the knowledgebase shown to staff during a call.
 * Each type carries an ordered list of questions to ask, actions to take and
 * information notes; "required" items are highlighted during the call.
 */
export function CallTypesPage() {
  const api = useApi();
  const [types, setTypes] = useState<CallType[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () => api.get<CallType[]>('/api/call-types').then(setTypes, (e: Error) => setError(e.message)),
    [api],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const run = (p: Promise<unknown>) => p.then(load).catch((e: Error) => setError(e.message));

  async function createType() {
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    await run(api.post('/api/call-types', { key, name }));
    setName('');
  }

  return (
    <>
      <h1>Call types &amp; knowledgebase</h1>
      {error && <p className="error">{error}</p>}
      <div className="filters">
        <input placeholder="New call type name" value={name} onChange={(e) => setName(e.target.value)} />
        <button className="primary" onClick={createType} disabled={!name.trim()}>Add call type</button>
      </div>
      {types.map((t) => (
        <TypeEditor key={t.id} type={t} onChange={(p) => run(p)} />
      ))}
    </>
  );
}

function TypeEditor({ type, onChange }: { type: CallType; onChange: (p: Promise<unknown>) => void }) {
  const api = useApi();
  const [text, setText] = useState('');
  const [kind, setKind] = useState<Kind>('question');
  const [required, setRequired] = useState(false);

  const items = [...type.items].filter((i) => i.isActive).sort((a, b) => a.sortOrder - b.sortOrder);

  function addItem() {
    onChange(api.post(`/api/call-types/${type.id}/items`, {
      kind, text, isRequired: required,
      sortOrder: (items[items.length - 1]?.sortOrder ?? 0) + 10,
    }));
    setText('');
    setRequired(false);
  }

  function move(idx: number, dir: -1 | 1) {
    const a = items[idx]!, b = items[idx + dir];
    if (!b) return;
    onChange(Promise.all([
      api.put(`/api/call-types/${type.id}/items/${a.id}`, { sortOrder: b.sortOrder }),
      api.put(`/api/call-types/${type.id}/items/${b.id}`, { sortOrder: a.sortOrder }),
    ]));
  }

  return (
    <section className="card">
      <h2>
        {type.name}
        {type.is_fallback && <span className="badge"> fallback</span>}
        {!type.is_fallback && (
          <button className="link danger" onClick={() => onChange(api.put(`/api/call-types/${type.id}`, { isActive: false }))}>
            deactivate
          </button>
        )}
      </h2>
      <ul className="kb">
        {items.map((i, idx) => (
          <li key={i.id}>
            <span className="reorder">
              <button className="link" disabled={idx === 0} onClick={() => move(idx, -1)}>↑</button>
              <button className="link" disabled={idx === items.length - 1} onClick={() => move(idx, 1)}>↓</button>
            </span>
            <div>
              <span className="badge">{i.kind}</span> {i.text}
              {i.isRequired && <strong className="required"> *required</strong>}
              <button className="link danger" onClick={() => onChange(api.put(`/api/call-types/${type.id}/items/${i.id}`, { isActive: false }))}>
                remove
              </button>
            </div>
          </li>
        ))}
        {items.length === 0 && <li><span /><em>No knowledgebase items yet — add the questions/actions below</em></li>}
      </ul>
      <div className="filters">
        <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input style={{ flex: 1 }} placeholder="e.g. Confirm the patient's date of birth" value={text} onChange={(e) => setText(e.target.value)} />
        <label><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> required</label>
        <button onClick={addItem} disabled={!text.trim()}>Add</button>
      </div>
    </section>
  );
}
